import { useState, useEffect } from 'react';
import { PropertyCard } from '@/components/PropertyCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['All', 'Plot', 'House', 'Commercial'];
const locations = ['All', 'Shivpuri', 'Jamul', 'Bhilai', 'Khedamara', 'Other'];
const statuses = ['All', 'Available', 'Booked', 'Sold', 'Coming Soon'];

export default function ProjectsPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'All',
    location: 'All',
    status: 'All',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(prop => {
    if (filters.category !== 'All' && prop.category !== filters.category) return false;
    if (filters.location !== 'All' && prop.location_label !== filters.location) return false;
    if (filters.status !== 'All' && prop.status !== filters.status) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        prop.title.toLowerCase().includes(searchLower) ||
        prop.code.toLowerCase().includes(searchLower) ||
        prop.location_label.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const clearFilters = () => {
    setFilters({ category: 'All', location: 'All', status: 'All', search: '' });
  };

  const hasActiveFilters = filters.category !== 'All' || filters.location !== 'All' || filters.status !== 'All' || filters.search;

  return (
    <div className="pt-16 lg:pt-20" data-testid="projects-page">
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-rust to-brand-orange py-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 text-center text-white">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-4">
            All Projects
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            Browse our complete portfolio of plots, houses, and commercial properties in the Jamul-Bhilai region
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 lg:top-20 z-30 bg-white border-b shadow-sm" data-testid="filters-section">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span>Filters</span>
              {hasActiveFilters && (
                <span className="bg-brand-orange text-white text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className={`${showFilters ? 'block' : 'hidden'} lg:flex lg:items-center lg:space-x-4`}>
            {/* Search */}
            <div className="relative flex-1 max-w-md mb-4 lg:mb-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, code, or location..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Category Filter */}
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger className="w-full lg:w-40 mb-4 lg:mb-0" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat === 'All' ? 'All Categories' : cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Location Filter */}
            <Select value={filters.location} onValueChange={(v) => setFilters({ ...filters, location: v })}>
              <SelectTrigger className="w-full lg:w-40 mb-4 lg:mb-0" data-testid="location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc} value={loc}>{loc === 'All' ? 'All Locations' : loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="w-full lg:w-40 mb-4 lg:mb-0" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((stat) => (
                  <SelectItem key={stat} value={stat}>{stat === 'All' ? 'All Status' : stat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear Filters - Desktop */}
            {hasActiveFilters && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={clearFilters}
                className="hidden lg:flex items-center text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Results Count */}
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredProperties.length}</span> properties
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
            </div>
          ) : filteredProperties.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-2xl">
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                No properties found
              </h3>
              <p className="text-gray-500 mb-4">
                Try adjusting your filters or search criteria
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear All Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
